import { Building2 } from "lucide-react";
import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { useOrganization } from "@/hooks/api/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { formatTaxId } from "@/lib/countryFormats";
import { countryKeyFromOrg } from "@/lib/countryProfile";
import { resolveDefaultRoute } from "@/lib/permissions";
import { stickyMediaUrl } from "@/lib/stableMediaUrl";
import { cn } from "@/lib/utils";

function isKnown(value: string | null | undefined): boolean {
  return value !== undefined;
}

/** Active organization in the header brand zone. Switching lives in the account menu. */
export function OrganizationSidebarSwitcher({
  compact = false,
}: {
  /** Icon-only — matches the collapsed sidebar rail width. */
  compact?: boolean;
}): ReactElement | null {
  const { user } = useAuth();
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);

  const activeOrg = user?.organization ?? null;
  const orgQuery = useOrganization({ enabled: Boolean(activeOrg) });
  const querySettled = orgQuery.isFetched || orgQuery.isError;
  const logoUrl = activeOrg
    ? stickyMediaUrl(activeOrg.id, activeOrg.logoUrl ?? orgQuery.data?.logoUrl ?? null)
    : null;
  const dataReady = Boolean(
    activeOrg &&
    activeOrg.name.trim() &&
    (isKnown(activeOrg.logoUrl) || querySettled) &&
    (isKnown(activeOrg.cnpj) || querySettled),
  );
  const imageLoaded = Boolean(logoUrl && loadedUrl === logoUrl);
  const showIdentity = dataReady && (!logoUrl || imageLoaded);

  useEffect(() => {
    if (!logoUrl) return;
    const probe = new window.Image();
    probe.src = logoUrl;
  }, [logoUrl]);

  if (!activeOrg) return null;

  const name = activeOrg.name.trim() || orgQuery.data?.name?.trim() || "";
  const taxId = (activeOrg.cnpj ?? orgQuery.data?.cnpj)?.trim();
  const subtitle = taxId ? formatTaxId(taxId, countryKeyFromOrg(activeOrg)) : "";
  const homeTo = resolveDefaultRoute(user);

  return (
    <Link
      to={homeTo}
      aria-busy={!showIdentity}
      aria-label={name || undefined}
      className={cn(
        "flex h-full min-w-0 items-center gap-2.5 whitespace-normal rounded-lg text-sidebar-foreground outline-none",
        "ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        compact && "justify-center",
      )}
    >
      <span
        className="relative flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted text-muted-foreground lg:size-10"
        aria-hidden
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt=""
            className={cn("size-full object-cover", showIdentity ? "opacity-100" : "opacity-0")}
            onLoad={() => setLoadedUrl(logoUrl)}
            onError={() => setLoadedUrl(logoUrl)}
          />
        ) : showIdentity ? (
          <Building2 className="size-4 lg:size-5" />
        ) : null}
      </span>
      {compact ? null : showIdentity ? (
        <span className="flex min-h-[2.125rem] min-w-0 max-w-[9rem] flex-col justify-center gap-0.5 text-left lg:max-w-[12rem]">
          <span className="line-clamp-1 min-w-0 break-words text-sm leading-tight font-medium">
            {name}
          </span>
          {subtitle ? (
            <span className="truncate text-xs leading-none text-muted-foreground">{subtitle}</span>
          ) : null}
        </span>
      ) : (
        <span className="flex min-h-[2.125rem] min-w-0 flex-col justify-center gap-1.5" aria-hidden>
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-2.5 w-24" />
        </span>
      )}
    </Link>
  );
}
